/**
 * Decode an audio Blob into normalized amplitude samples for waveform rendering.
 * Returns a Float32Array of `barCount` values in the 0..1 range.
 */
export async function decodeAudioToSamples(blob, barCount = 60) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)()
  try {
    const buf = await blob.arrayBuffer()
    const audioBuffer = await ctx.decodeAudioData(buf)
    const raw = audioBuffer.getChannelData(0)
    const samplesPerBar = Math.floor(raw.length / barCount)
    const result = new Float32Array(barCount)
    let max = 0
    for (let i = 0; i < barCount; i++) {
      let sum = 0
      const start = i * samplesPerBar
      const end = Math.min(start + samplesPerBar, raw.length)
      for (let j = start; j < end; j++) {
        sum += raw[j] * raw[j]
      }
      result[i] = Math.sqrt(sum / (end - start))
      if (result[i] > max) max = result[i]
    }
    if (max > 0) for (let i = 0; i < barCount; i++) result[i] /= max
    return { samples: result, duration: audioBuffer.duration }
  } finally {
    ctx.close()
  }
}

/**
 * Draw a bar-style waveform on a canvas.
 * @param {HTMLCanvasElement} canvas
 * @param {Float32Array|number[]} samples  - normalized 0..1 amplitudes
 * @param {object} opts
 * @param {number} [opts.progress=0]       - 0..1 playback progress for color split
 * @param {string} [opts.playedColor]      - color for played bars
 * @param {string} [opts.unplayedColor]    - color for unplayed bars
 */
/**
 * Compute how many bars fit in a given pixel width.
 */
export function barsForWidth(width) {
  const step = 2.5 + 1.5 // barW + gap
  return Math.max(10, Math.floor(width / step))
}

export function drawWaveform(canvas, samples, opts = {}) {
  const { progress = 0, fill = false, playedColor = '#7a9a7a', unplayedColor = 'rgba(255,255,255,0.35)' } = opts
  const dpr = window.devicePixelRatio || 1
  const rect = canvas.getBoundingClientRect()
  const w = rect.width
  const h = rect.height
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
  }
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.scale(dpr, dpr)

  const totalBars = samples.length
  const progressBar = Math.floor(progress * totalBars)

  // fill=true: stretch bars to fill canvas width (for inline players)
  // fill=false: fixed bar sizing, centered (for review/recording)
  const barW = fill ? Math.max(1.5, (w / totalBars) * 0.6) : 2.5
  const gap = fill ? 0 : 1.5
  const step = fill ? (w / totalBars) : (barW + gap)
  const totalWidth = fill ? w : totalBars * step
  const offsetX = fill ? 0 : Math.max(0, (w - totalWidth) / 2)

  for (let i = 0; i < totalBars; i++) {
    const barH = Math.max(2, samples[i] * (h - 4))
    const x = offsetX + (fill ? i * step + (step - barW) / 2 : i * step)
    const y = (h - barH) / 2
    ctx.fillStyle = i < progressBar ? playedColor : unplayedColor
    ctx.beginPath()
    ctx.roundRect(x, y, barW, barH, barW / 2)
    ctx.fill()
  }

  ctx.restore()
}
